import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../api/client.js";
import type { User } from "../api/types.js";

/**
 * 会话上下文。
 *
 * 两个事实由后端回答，且**只在这里问一次**：
 * - `initialized`：是否已经完成一次性初始化（决定进 setup 还是登录）
 * - `me`：当前会话是谁（决定是否进应用）
 *
 * `me` 在未登录时后端返回 401 —— 那不是错误，是「匿名」这个正常状态，
 * 所以在这里折成 `null`，不让它变成一个红色的查询错误。
 */

interface AuthValue {
  /** `null` = 未登录。 */
  user: User | null;
  /** 是否已完成初始化。`null` = 还没问出来。 */
  initialized: boolean | null;
  /** 首次加载（setup 状态或 me 还没回来）。 */
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** 初始化成功后刷新「已初始化」与「我是谁」。 */
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const setupQuery = useQuery({
    queryKey: ["setup-status"],
    queryFn: api.setupStatus,
    staleTime: Infinity,
  });

  const meQuery = useQuery({
    queryKey: ["me"],
    queryFn: async (): Promise<User | null> => {
      try {
        return (await api.me()).user;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    retry: false,
  });

  const login = useCallback(
    async (username: string, password: string) => {
      const { user } = await api.login(username, password);
      queryClient.setQueryData(["me"], user);
      // 配对状态、设置、列表都可能随身份变化 —— 整棵缓存重新取。
      await queryClient.invalidateQueries();
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    await api.logout();
    queryClient.setQueryData(["me"], null);
    queryClient.clear();
    queryClient.setQueryData(["me"], null);
  }, [queryClient]);

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries();
  }, [queryClient]);

  const value = useMemo<AuthValue>(
    () => ({
      user: meQuery.data ?? null,
      initialized: setupQuery.data?.initialized ?? null,
      loading: setupQuery.isLoading || meQuery.isLoading,
      login,
      logout,
      refresh,
    }),
    [
      meQuery.data,
      meQuery.isLoading,
      setupQuery.data,
      setupQuery.isLoading,
      login,
      logout,
      refresh,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) throw new Error("useAuth 必须在 AuthProvider 内使用");
  return ctx;
}
